import React, { createContext, useContext, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getSelectedHotel } from "../actions/hotel";

const SessionTrackingUsagestatsContext = createContext(null);

const selectAppUID = (state) => state.identity.appUID;
const selectUID = (state) => state.identity.uid;

const SESSION_ID_KEY = "app_session_id";
const APP_OPEN_SENT_KEY = "app_open_sent";
const ANONYMOUS_ACCESS_PENDING_KEY = "anonymous_access_pending";
const SIGN_IN_PENDING_KEY = "usage_stats_sign_in_pending";
const SIGN_UP_PENDING_KEY = "usage_stats_sign_up_pending";

const generateSessionId = (deviceRef) => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, "0");

    const getDatetime = () =>
        [now.getUTCFullYear(), pad(now.getUTCMonth() + 1), pad(now.getUTCDate())].join("") +
        "T" +
        [pad(now.getUTCHours()), pad(now.getUTCMinutes()), pad(now.getUTCSeconds())].join("");

    return `${deviceRef}:${getDatetime()}`;
};

const getStoredSessionId = () => {
    try {
        return sessionStorage.getItem(SESSION_ID_KEY);
    } catch (error) {
        console.error("Error reading sessionId from sessionStorage:", error);
        return null;
    }
};

const setStoredSessionId = (sessionId) => {
    try {
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    } catch (error) {
        console.error("Error saving sessionId to sessionStorage:", error);
    }
};

const getAppOpenSent = () => {
    try {
        return sessionStorage.getItem(APP_OPEN_SENT_KEY) === "true";
    } catch (error) {
        console.error("Error reading appOpenSent from sessionStorage:", error);
        return false;
    }
};

const setAppOpenSent = (value) => {
    try {
        sessionStorage.setItem(APP_OPEN_SENT_KEY, value ? "true" : "false");
    } catch (error) {
        console.error("Error saving appOpenSent to sessionStorage:", error);
    }
};

const getAnonymousAccessPending = () => {
    try {
        return sessionStorage.getItem(ANONYMOUS_ACCESS_PENDING_KEY) === "true";
    } catch (error) {
        console.error("Error reading anonymousAccessPending from sessionStorage:", error);
        return false;
    }
};

const setAnonymousAccessPending = (value) => {
    try {
        sessionStorage.setItem(ANONYMOUS_ACCESS_PENDING_KEY, value ? "true" : "false");
    } catch (error) {
        console.error("Error saving anonymousAccessPending to sessionStorage:", error);
    }
};

const getPendingUsageEvent = (key) => {
    try {
        const rawValue = sessionStorage.getItem(key);
        return rawValue ? JSON.parse(rawValue) : null;
    } catch (error) {
        console.error(`Error reading ${key} from sessionStorage:`, error);
        return null;
    }
};

const setPendingUsageEvent = (key, value) => {
    try {
        if (value) {
            sessionStorage.setItem(key, JSON.stringify(value));
        } else {
            sessionStorage.removeItem(key);
        }
    } catch (error) {
        console.error(`Error saving ${key} to sessionStorage:`, error);
    }
};

export const SessionTrackingUsagestatsProvider = ({ children }) => {
    const appUID = useSelector(selectAppUID);
    const uid = useSelector(selectUID);
    const project = useSelector(getSelectedHotel);

    const [appOpenSent, setAppOpenSentState] = useState(() => getAppOpenSent());
    const [anonymousAccessPending, setAnonymousAccessPendingState] = useState(() => getAnonymousAccessPending());
    const [pendingSignInTracking, setPendingSignInTracking] = useState(() => getPendingUsageEvent(SIGN_IN_PENDING_KEY));
    const [pendingSignUpTracking, setPendingSignUpTracking] = useState(() => getPendingUsageEvent(SIGN_UP_PENDING_KEY));

    const sessionId = useMemo(() => {
        const deviceRef = `${appUID}:${uid}`;

        const storedSessionId = getStoredSessionId();

        if (storedSessionId && storedSessionId.startsWith(deviceRef)) {
            return storedSessionId;
        }

        const newSessionId = generateSessionId(deviceRef);
        setStoredSessionId(newSessionId);

        setAppOpenSent(false);
        setAppOpenSentState(false);
        setAnonymousAccessPending(false);
        setAnonymousAccessPendingState(false);
        setPendingUsageEvent(SIGN_IN_PENDING_KEY, null);
        setPendingSignInTracking(null);
        setPendingUsageEvent(SIGN_UP_PENDING_KEY, null);
        setPendingSignUpTracking(null);

        return newSessionId;
    }, [appUID, uid]);

    const projectRef = project?.ref;

    const markAppOpenAsSent = useCallback(() => {
        setAppOpenSent(true);
        setAppOpenSentState(true);
    }, []);

    const markAnonymousAccessAsPending = useCallback(() => {
        setAnonymousAccessPending(true);
        setAnonymousAccessPendingState(true);
    }, []);

    const markAnonymousAccessAsSent = useCallback(() => {
        setAnonymousAccessPending(false);
        setAnonymousAccessPendingState(false);
    }, []);

    const markSignInTrackingAsPending = useCallback((eventData) => {
        setPendingUsageEvent(SIGN_IN_PENDING_KEY, eventData);
        setPendingSignInTracking(eventData);
    }, []);

    const markSignInTrackingAsSent = useCallback(() => {
        setPendingUsageEvent(SIGN_IN_PENDING_KEY, null);
        setPendingSignInTracking(null);
    }, []);

    const markSignUpTrackingAsPending = useCallback((eventData) => {
        setPendingUsageEvent(SIGN_UP_PENDING_KEY, eventData);
        setPendingSignUpTracking(eventData);
    }, []);

    const markSignUpTrackingAsSent = useCallback(() => {
        setPendingUsageEvent(SIGN_UP_PENDING_KEY, null);
        setPendingSignUpTracking(null);
    }, []);

    return (
        <SessionTrackingUsagestatsContext.Provider
            value={{
                sessionId,
                projectRef,
                appOpenSent,
                markAppOpenAsSent,
                anonymousAccessPending,
                markAnonymousAccessAsPending,
                markAnonymousAccessAsSent,
                pendingSignInTracking,
                pendingSignUpTracking,
                markSignInTrackingAsPending,
                markSignInTrackingAsSent,
                markSignUpTrackingAsPending,
                markSignUpTrackingAsSent,
            }}
        >
            {children}
        </SessionTrackingUsagestatsContext.Provider>
    );
};

export const useSessionTrackingUsagestats = () => {
    const context = useContext(SessionTrackingUsagestatsContext);
    if (!context) {
        throw new Error("useSessionTrackingUsagestats must be used within a SessionTrackingUsagestatsProvider");
    }
    return context;
};
